
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AssistantData } from "@/types/assistant";
import { useToast } from "@/components/ui/use-toast";

export const useAssistantData = (urlSlug = "midnight-hotel") => {
  const [assistantData, setAssistantData] = useState<AssistantData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [connectionError, setConnectionError] = useState<boolean>(false);
  const [notFoundError, setNotFoundError] = useState<boolean>(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchAssistantData = async () => {
      try {
        setIsLoading(true);
        setConnectionError(false);
        setNotFoundError(false);
        
        const slug = urlSlug || "midnight-hotel";
        console.log("Fetching assistant data with slug:", slug);
        
        
        // First, try to fetch the exact slug
        const { data: assistantDataResult, error: assistantError } = await supabase
          .from("assistant_data")
          .select(`
            *,
            hotels!assistant_data_hotel_id_fkey(
              id,
              name,
              address,
              hotel_info
            )
          `)
          .eq("url_slug", slug)
          .maybeSingle();

        if (assistantError) {
          console.error("Database query error for slug", slug, ":", assistantError);
          
          if (assistantError.message?.includes('Failed to fetch') || 
              assistantError.message?.includes('Network Error') ||
              assistantError.code === 'PGRST301') {
            setConnectionError(true);
          }
          
          throw assistantError;
        }

        let finalAssistantData = assistantDataResult;
        let usedFallback = false;

        // If no exact match found and slug is "alfie-demo", try to get a random enabled assistant
        if (!assistantDataResult && slug === "alfie-demo") {
          console.log("No exact match for 'alfie-demo', trying random enabled assistant...");
          
          const { data: randomAssistantData, error: randomError } = await supabase
            .from("assistant_data")
            .select(`
              *,
              hotels!assistant_data_hotel_id_fkey(
                id,
                name,
                address,
                hotel_info
              )
            `)
            .eq("enabled", true)
            .order('id', { ascending: false }) // Use a more predictable order than RANDOM()
            .limit(1)
            .maybeSingle();

          if (randomError) {
            console.error("Error fetching random assistant:", randomError);
          } else if (randomAssistantData) {
            finalAssistantData = randomAssistantData;
            usedFallback = true;
            console.log("Using random assistant fallback:", randomAssistantData.url_slug);
            
            // Get hotel name for the toast
            const hotelName = randomAssistantData.hotels?.name || "Demo Hotel";
            
            // Show info toast about the fallback
            toast({
              title: "Demo Mode",
              description: `Using ${hotelName} for demo, refresh page to use another hotel demo`,
              variant: "default",
            });
          }
        }

        console.log("Assistant data query result for slug", slug, ":", finalAssistantData);
        
        if (!finalAssistantData) {
          console.log("No assistant found with slug:", slug);
          setNotFoundError(true);
          setAssistantData(null);
          setIsLoading(false);
          return;
        }
        
        // Extract hotel data from joined result
        const hotelData = finalAssistantData.hotels;
          
        // Transform the database data to match the AssistantData type
        const transformedData: AssistantData = {
          id: finalAssistantData.id,
          hotel_id: finalAssistantData.hotel_id,
          url_slug: finalAssistantData.url_slug,
          enabled: finalAssistantData.enabled,
          assistant_data: {
            // Safely extract assistant settings
            ...(finalAssistantData.data_assistant_settings && typeof finalAssistantData.data_assistant_settings === 'object' ? finalAssistantData.data_assistant_settings : {}),
            
            // Use hotel data from hotels table if available, otherwise fall back to data_hotel_info
            hotel_details: hotelData 
              ? {
                  name: hotelData.name as string || null,
                  address: hotelData.address as string || null,
                  amenities: Array.isArray((hotelData.hotel_info as any)?.amenities) ? (hotelData.hotel_info as any).amenities as string[] : null,
                  phone: (hotelData.hotel_info as any)?.phone as string || null
                }
              : finalAssistantData.data_hotel_info && typeof finalAssistantData.data_hotel_info === 'object' && !Array.isArray(finalAssistantData.data_hotel_info)
                ? {
                    name: finalAssistantData.data_hotel_info.name as string || null,
                    address: finalAssistantData.data_hotel_info.address as string || null,
                    amenities: Array.isArray(finalAssistantData.data_hotel_info.amenities) ? finalAssistantData.data_hotel_info.amenities as string[] : null,
                    phone: finalAssistantData.data_hotel_info.phone as string || null
                  }
                : {},
            
            // Store raw data for debugging and future access
            raw_hotel_info: hotelData || 
                           (finalAssistantData.data_hotel_info && typeof finalAssistantData.data_hotel_info === 'object' ? finalAssistantData.data_hotel_info : {}),
            raw_assistant_settings: finalAssistantData.data_assistant_settings && typeof finalAssistantData.data_assistant_settings === 'object' ? finalAssistantData.data_assistant_settings : {},
            raw_recommendations: finalAssistantData.data_recommendations && typeof finalAssistantData.data_recommendations === 'object' ? finalAssistantData.data_recommendations : {},
            recommendations: finalAssistantData.data_recommendations && typeof finalAssistantData.data_recommendations === 'object' ? finalAssistantData.data_recommendations : {}
          },
          created_at: finalAssistantData.created_at,
          updated_at: finalAssistantData.updated_at
        };
        
        setAssistantData(transformedData);
        console.log("Transformed assistant data with hotel info:", transformedData);
        
        if (usedFallback) {
          console.log("Used fallback assistant for 'alfie-demo':", transformedData.url_slug);
        }
        
        // Log the variables available for debugging
        console.log("Variables available for prompt replacement:", {
          assistantName: transformedData.assistant_data.assistantName || "N/A",
          hotelName: transformedData.assistant_data.hotel_details?.name || "N/A",
          hotelAddress: transformedData.assistant_data.hotel_details?.address || "N/A",
          hotelPhone: transformedData.assistant_data.hotel_details?.phone || "N/A",
          hotelAmenities: transformedData.assistant_data.hotel_details?.amenities || "N/A",
          hasHotelInfo: !!transformedData.assistant_data.raw_hotel_info && Object.keys(transformedData.assistant_data.raw_hotel_info).length > 0,
          hasRecommendations: !!transformedData.assistant_data.raw_recommendations && Object.keys(transformedData.assistant_data.raw_recommendations).length > 0,
          usedFallback: usedFallback
        });
        
      } catch (err) {
        console.error("Error fetching assistant data for slug", urlSlug, ":", err);
        setError(err instanceof Error ? err : new Error("Failed to fetch assistant data"));
        
        if (err.message?.includes('Failed to fetch') || 
            err.message?.includes('Network Error') ||
            err.code === 'PGRST301') {
          setConnectionError(true);
        }
        
        setAssistantData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssistantData();
  }, [urlSlug, toast]);

  return { 
    assistantData, 
    isLoading, 
    error,
    connectionError,
    notFoundError
  };
};
